import { FC, useEffect, useState, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import HelpTip from 'components/Common/Tooltips/HelpTip'
import LoadedImage from './LoadedImage'
import DragZone from './DragZone'
import Empty from './Empty'
import { Wrapper, Title } from './_styles'

interface UploadItemProps {
  title?: string
  value: File | null
  onChange: (value: File | FileList | null) => void
  hasError?: boolean
  tooltip?: string
  loading?: boolean
  multiple?: boolean
  'data-testid'?: string
}

const UploadItem: FC<UploadItemProps> = props => {
  const { t } = useTranslation()
  const {
    title,
    value,
    onChange,
    hasError,
    tooltip,
    loading,
    multiple,
    'data-testid': dataTestId,
  } = props
  const [_loading, setLoading] = useState(loading || false)
  const [imageUrl, setImageUrl] = useState<string>('')
  const isLoadingControlled = useRef(loading !== undefined)

  const handleChange = async (sourceFile: File | FileList | string | null) => {
    if (!sourceFile) {
      onChange(null)
      setImageUrl('')
      return
    }

    if (typeof sourceFile === 'string') {
      return
    }

    // Устанавливаем loading только если родитель не управляет состоянием
    if (!isLoadingControlled.current) {
      setLoading(true)
    }
    onChange(sourceFile)
  }

  useEffect(() => {
    if (value instanceof File) {
      return setImageUrl(URL.createObjectURL(value))
    } else if (value) {
      return setImageUrl(value)
    }
  }, [value])

  useEffect(() => {
    // Если loading контролируется родителем, синхронизируем состояние
    if (isLoadingControlled.current) {
      setLoading(loading || false)
    }
  }, [loading])

  return (
    <Wrapper data-testid={dataTestId}>
      {title && (
        <>
          {tooltip ? (
            <Title hasError={hasError}>
              {title}
              <HelpTip width={180}>
                <Trans t={t} i18nKey={tooltip} />
              </HelpTip>
            </Title>
          ) : (
            <Title hasError={hasError}>{title}</Title>
          )}
        </>
      )}
      <DragZone
        loading={_loading}
        onChange={handleChange}
        hasError={hasError}
        empty={!value}
      >
        {imageUrl ? (
          <LoadedImage value={imageUrl} onChange={handleChange} />
        ) : (
          <Empty onChange={handleChange} multiple={multiple} />
        )}
      </DragZone>
    </Wrapper>
  )
}

export default UploadItem
